import React, { useRef, useState } from 'react';
import I18n from 'i18n-js';
import Popover from '@/components/ui/Popover';
import Icon from '@/components/ui/Icon';
import StandardsTag from '@/components/shared/StandardsPills/StandardsTag';
import { Standard } from '@/components/shared/StandardsPills/types';
import cn from '@/utils/cn';
import stripStandardsAliasPrefix from '@/utils/stripStandardsAliasPrefix';
import Text from '@/components/ui/Text';

type Props = {
  heading: string;
  locale: Locale;
  standards: Standard[];
};

// React 16 has no useId, and a Unit Overview renders one of these per card.
let headingIdCounter = 0;

const StandardsDetailsPopover = ({ heading, locale, standards }: Props) => {
  const contentRef = useRef<HTMLDivElement>(null);
  const [headingId] = useState(() => {
    headingIdCounter += 1;
    return `standards-correlation-title-${headingIdCounter}`;
  });

  return (
    <Popover modal>
      <Popover.Trigger asChild>
        <button
          className={cn('tw-inline-flex tw-items-center tw-bg-transparent tw-p-0 tw-ml-1',
            'tw-border-0 tw-border-b tw-border-solid tw-border-current',
            'tw-text-tci-purple tw-cursor-pointer tw-whitespace-nowrap',
            'tw-font-tci tw-text-xs tw-font-semibold',
            'focus-visible:tw-outline focus-visible:tw-outline-2 focus-visible:tw-outline-tci-purple',
            'focus-visible:tw-rounded')}
          type="button"
        >
          {I18n.t('see_details')} <Icon icon="ArrowRight" size={16} />
        </button>
      </Popover.Trigger>
      {/* AIDEV-NOTE: Content itself is the scroll container, not the <ul>. onOpenAutoFocus puts focus
          here, so PageDown/arrows scroll the list without adding a tab stop — a scrollable region the
          keyboard can't reach is WCAG 2.1.1. ui/Popover sets tw-overflow-hidden; twMerge keeps both,
          giving overflow-x hidden + overflow-y auto. Radix computes the available height for us. */}
      <Popover.Content
        ref={contentRef}
        align="end"
        aria-labelledby={headingId}
        className="tw-w-[640px] tw-p-0 tw-overflow-y-auto tw-max-h-[var(--radix-popover-content-available-height)]"
        side="bottom"
        sideOffset={10}
        tabIndex={-1}
        width="unset"
        onOpenAutoFocus={(e) => {
          e.preventDefault();
          contentRef.current?.focus();
        }}
      >
        <div className={cn('tw-flex tw-items-center tw-justify-between tw-px-4 tw-py-2 tw-bg-light-grey',
          'tw-border-b tw-border-gray-200 tw-sticky tw-top-0 tw-z-10')}
        >
          <Text className="tw-m-0 tw-font-tci tw-text-lg" id={headingId} weight="semibold">
            {heading}
          </Text>
          <Popover.Close
            aria-label={I18n.t('close')}
            className={cn('tw-h-7 tw-w-7 tw-flex tw-items-center tw-justify-center tw-rounded',
              'tw-bg-transparent tw-border-0 tw-cursor-pointer tw-text-gray-700 hover:tw-bg-gray-100',
              'focus-visible:tw-outline focus-visible:tw-outline-2 focus-visible:tw-outline-tci-purple')}
          >
            <Icon icon="Close" size={16} />
          </Popover.Close>
        </div>
        <ul className="tw-list-none tw-grid tw-grid-cols-[auto_1fr] tw-gap-x-4 tw-gap-y-3 tw-m-0 tw-p-4">
          {standards.map((standard) => {
            const description = locale === 'es' && standard.description_es
              ? standard.description_es
              : standard.description_en;
            return (
              <li key={standard.id} className="tw-contents">
                <StandardsTag alias={standard.alias} />
                <Text className="tw-font-tci">
                  {stripStandardsAliasPrefix(description, standard.alias)}
                </Text>
              </li>
            );
          })}
        </ul>
      </Popover.Content>
    </Popover>
  );
};

export default StandardsDetailsPopover;
